import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCcw, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { logFrontendError, logCriticalError } from "@/lib/errorReporting";
import { logger } from "@/lib/logger";
import { sanitizeErrorMessage, sanitizeStackTrace, shouldReportError } from "@/lib/errorSanitizer";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  retryCount: number;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    retryCount: 0
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, retryCount: 0 };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to logger in development only
    if (import.meta.env.DEV) {
      logger.error('ErrorBoundary caught an error', {
        error,
        componentStack: errorInfo.componentStack,
      });
    }
    
    // Only report errors that should be tracked
    if (shouldReportError(error)) {
      logger.error("ErrorBoundary caught error", {
        error: sanitizeErrorMessage(error),
        stack: sanitizeStackTrace(error.stack),
        componentStack: import.meta.env.DEV ? errorInfo.componentStack : undefined
      });
      
      // Log to error tracking with sanitized context
      logFrontendError(error, {
        componentStack: import.meta.env.DEV ? errorInfo.componentStack : undefined,
        errorBoundary: true,
        retryCount: this.state.retryCount,
        url: window.location.href,
      });
    }

    this.setState({ errorInfo });
  }

  private handleReset = () => {
    const newRetryCount = this.state.retryCount + 1;
    
    logger.info(`ErrorBoundary: Retry attempt ${newRetryCount}`);
    
    // Exponential backoff for retries
    if (newRetryCount > 3) {
      logCriticalError("Too many retry attempts", {
        retryCount: newRetryCount,
        error: this.state.error?.message,
      });
      return;
    }
    
    this.setState({ 
      hasError: false, 
      error: undefined, 
      errorInfo: undefined,
      retryCount: newRetryCount 
    });
  };

  private handleReportError = () => {
    const errorDetails = {
      message: this.state.error?.message,
      stack: this.state.error?.stack,
      componentStack: this.state.errorInfo?.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };

    logCriticalError("User reported error", errorDetails);
    
    // Show confirmation to user
    alert("Error reported. Our team will investigate. Thank you!");
  };

  private getErrorType(error?: Error): {
    type: 'network' | 'auth' | 'notfound' | 'permission' | 'module' | 'generic';
    title: string;
    message: string;
    action: string;
  } {
    const errorMessage = error?.message?.toLowerCase() || '';
    const errorStack = error?.stack?.toLowerCase() || '';

    // Network errors
    if (errorMessage.includes('network') || 
        errorMessage.includes('failed to fetch') ||
        errorMessage.includes('timeout')) {
      return {
        type: 'network',
        title: 'Connection Lost',
        message: 'Unable to connect to the server. Please check your internet connection and try again.',
        action: 'Retrying...'
      };
    }

    // Auth errors
    if (errorMessage.includes('unauthorized') || 
        errorMessage.includes('auth') ||
        errorMessage.includes('session expired') ||
        errorStack.includes('401')) {
      return {
        type: 'auth',
        title: 'Session Expired',
        message: 'Your session has expired. Please log in again to continue.',
        action: 'Redirecting to login...'
      };
    }

    // Not found errors
    if (errorMessage.includes('not found') || errorStack.includes('404')) {
      return {
        type: 'notfound',
        title: 'Page Not Found',
        message: "The page you're looking for doesn't exist or has been moved.",
        action: 'Go to homepage'
      };
    }

    // Permission errors
    if (errorMessage.includes('permission') || 
        errorMessage.includes('forbidden') ||
        errorStack.includes('403')) {
      return {
        type: 'permission',
        title: 'Access Denied',
        message: "You don't have permission to access this resource.",
        action: 'Go back'
      };
    }

    // Module loading errors
    if (errorMessage.includes('module') || 
        errorMessage.includes('import') ||
        errorMessage.includes('chunk')) {
      return {
        type: 'module',
        title: 'Loading Issue',
        message: 'There was a problem loading the page. This usually resolves with a refresh.',
        action: 'Refresh page'
      };
    }

    return {
      type: 'generic',
      title: 'Something Went Wrong',
      message: error?.message || 'An unexpected error occurred. Please try again.',
      action: 'Try again'
    };
  }

  private handleErrorAction = () => {
    const errorType = this.getErrorType(this.state.error);

    // Enhanced recovery strategies based on error type
    switch (errorType.type) {
      case 'auth':
        // Clear auth state and redirect
        if (typeof window !== 'undefined' && 'localStorage' in window) {
          localStorage.removeItem('sb-auth-token');
          sessionStorage.clear();
        }
        window.location.href = '/auth'; // Hard reload needed - session lost
        break;
      case 'notfound':
        window.location.href = '/'; // Hard reload needed - invalid route
        break;
      case 'permission':
        // Try to go back, fallback to home
        if (window.history.length > 1) {
          window.history.back();
        } else {
          window.location.href = '/';
        }
        break;
      case 'module':
        // Try to reload the specific module first
        if (this.state.retryCount < 2) {
          this.handleReset();
        } else {
          window.location.reload(); // Hard reload after retries exhausted
        }
        break;
      case 'network':
        // For network errors, try soft reset first, then hard reload
        if (this.state.retryCount < 2) {
          this.handleReset();
        } else {
          // Check if online before reloading
          if (navigator.onLine) {
            window.location.reload();
          } else {
            // Show offline message
            alert('You appear to be offline. Please check your connection and try again.');
          }
        }
        break;
      default:
        // Generic errors: try soft reset first
        this.handleReset();
    }
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const errorType = this.getErrorType(this.state.error);
      const showRetryLimit = this.state.retryCount >= 3;

      return (
        <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
          <div className="w-full max-w-md rounded-2xl border bg-card p-8 text-center shadow-xl">
            <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="size-8 text-destructive" />
            </div>
            
            <h2 className="mb-4 break-words text-2xl font-bold">{errorType.title}</h2>
            <p className="mb-6 break-words text-muted-foreground">
              {import.meta.env.PROD && this.state.error
                ? sanitizeErrorMessage(this.state.error)
                : errorType.message}
            </p>

            {showRetryLimit && (
              <div className="mb-4 rounded-lg border border-warning/20 bg-warning/5 p-4 dark:border-amber-800 dark:bg-warning/20">
                <div className="flex items-center gap-2 text-warning-foreground dark:text-amber-200">
                  <AlertCircle className="size-4" />
                  <p className="text-sm font-medium">Too many retry attempts</p>
                </div>
                <p className="mt-1 text-xs text-warning dark:text-amber-300">
                  Please refresh the page or contact support if the issue persists.
                </p>
              </div>
            )}
            
            <div className="mb-4 flex justify-center gap-4">
              {!showRetryLimit && (
                <Button onClick={this.handleErrorAction} className="gap-2">
                  <RefreshCcw className="size-4" />
                  {errorType.action}
                </Button>
              )}
              
              {errorType.type !== 'notfound' && (
                <Button 
                  onClick={() => window.location.href = '/'} 
                  variant={showRetryLimit ? "default" : "outline"}
                >
                  Go Home
                </Button>
              )}
            </div>

            <Button 
              onClick={this.handleReportError} 
              variant="ghost" 
              size="sm"
              className="text-muted-foreground hover:text-foreground"
            >
              Report Error
            </Button>
            
            {import.meta.env.DEV && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                  Error details (dev only)
                </summary>
                <div className="mt-2 space-y-2">
                  <pre className="max-h-32 overflow-auto rounded bg-muted p-4 text-xs">
                    {this.state.error.message}
                  </pre>
                  <pre className="max-h-32 overflow-auto rounded bg-muted p-4 text-xs">
                    {this.state.error.stack}
                  </pre>
                  {this.state.errorInfo?.componentStack && (
                    <pre className="max-h-32 overflow-auto rounded bg-muted p-4 text-xs">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
